import { type Actor } from '@shared/board'
import { useSyncExternalStore } from 'react'
import { useBoardCtx } from '@/board/context'
import { useI18n } from '@/i18n/i18n'

/** How to name a person or a conversation to the viewer: "你", "協作者", "對話「…」". */
export function useActorName() {
  const { me, sessions } = useBoardCtx()
  const { t } = useI18n()
  const snap = useSyncExternalStore(sessions.subscribe, sessions.getSnapshot)
  return (a: Actor | null) => {
    if (!a) return t('board.someone')
    if (a.kind === 'user') return a.id === me.id ? t('author.me') : t('author.someone')
    const s = snap.sessions.find((x) => x.id === a.id)
    // An AI conversation that is not on this board (e.g. from an imported board): just "AI".
    if (!s) return t('board.ai')
    return t('board.session', { title: s.title || t('session.untitled') })
  }
}

/**
 * Who reads and replies in a conversation, as its own lines say it: "AI", or
 * the outside app that drives it ("Claude Code").
 */
export function useSpeaker(sessionId: string | null): string {
  const { sessions } = useBoardCtx()
  const { t } = useI18n()
  const snap = useSyncExternalStore(sessions.subscribe, sessions.getSnapshot)
  return snap.sessions.find((s) => s.id === sessionId)?.external?.client ?? t('chat.theAi')
}
